import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import { MRT_Localization_PT_BR } from "material-react-table/locales/pt-BR";
import { Button, Divider, Drawer, Space, Tabs, Tag, Tooltip, Typography } from "antd";
import { Content } from "antd/es/layout/layout";
import { AiFillDelete, AiFillEdit, AiOutlineSearch } from "react-icons/ai";
import { FaPlay, FaPlus } from "react-icons/fa";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import { request } from "../../../functions/request";
import Aguarde from "../../../components/aguarde/aguarde";
import ModalAddAutomacao from "../../../components/Modal/Automacao/ModalAddAutomacao";

const { Text } = Typography;

const SECOES = {
  1: "Boi", 2: "Frango", 3: "Grãos", 4: "Leite", 5: "Suinos",
  8: "Agroações", 9: "Bolsas e Opções",
};

const NOME_DIAS = { 0: "Dom", 1: "Seg", 2: "Ter", 3: "Qua", 4: "Qui", 5: "Sex", 6: "Sáb" };

const CORES_STATUS = {
  publicada: "green",
  rascunho: "blue",
  bloqueada: "orange",
  pulada: "default",
  erro: "red",
  processando: "processing",
};

/**
 * Automações de notícia: o que roda sozinho, em que horário, e o que
 * aconteceu em cada execução.
 *
 * O histórico é tão importante quanto o cadastro: como a matéria sai sem
 * ninguém olhar, é aqui que se confere o que foi publicado e por que uma
 * execução foi bloqueada.
 */
export default function Automacoes() {
  const [automacoes, setAutomacoes] = useState([]);
  const [execucoes, setExecucoes] = useState([]);
  const [carregou, setCarregou] = useState(false);
  const [formAberto, setFormAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState({});
  const [htmlAberto, setHtmlAberto] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const [lista, historico] = await Promise.all([
        request("/automacao/listar", { method: "GET" }),
        request("/automacao/execucoes?limite=100", { method: "GET" }),
      ]);
      setAutomacoes(lista);
      setExecucoes(historico);
      setCarregou(true);
    } catch (erro) {
      console.log(erro);
      toast.error("Não foi possível carregar as automações.");
      setCarregou(true);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const excluir = (linha) =>
    confirmAlert({
      title: `Excluir a automação "${linha.aut_nome}"?`,
      message: "O histórico de execuções dela também será apagado.",
      buttons: [
        { label: "Não", className: "button-delete", onClick: () => {} },
        {
          label: "Sim",
          className: "button-save",
          onClick: async () => {
            try {
              await request(`/automacao/excluir/${linha.aut_codigo}`, { method: "DELETE" });
              toast.success("Automação excluída.");
              carregar();
            } catch (erro) {
              console.log(erro);
              toast.error("Erro ao excluir.");
            }
          },
        },
      ],
    });

  /**
   * Rodar agora usa uma referência no passado para não consumir a
   * execução do dia: dá para testar de manhã sem impedir que a
   * automação rode no horário dela.
   */
  const rodarAgora = (linha) =>
    confirmAlert({
      title: `Rodar "${linha.aut_nome}" agora?`,
      message:
        linha.aut_modo === "publicar"
          ? "A matéria será publicada de verdade, se passar nas verificações."
          : "Será criado um rascunho, se passar nas verificações.",
      buttons: [
        { label: "Cancelar", className: "button-delete", onClick: () => {} },
        {
          label: "Rodar",
          className: "button-save",
          onClick: async () => {
            try {
              const marca = `teste-${Date.now()}`.slice(0, 10);
              const r = await request(
                `/automacao/executar/${linha.aut_codigo}?referencia=${marca}`,
                { method: "POST" }
              );
              if (r.status === "publicada") toast.success(`Publicada! Notícia ${r.noticia}.`);
              else if (r.status === "rascunho") toast.success(`Rascunho criado (notícia ${r.noticia}).`);
              else if (r.status === "bloqueada") toast.warning(`Bloqueada: ${r.problemas.join(" | ")}`);
              else toast.error(`Não executou: ${r.erro || r.status}`);
              carregar();
            } catch (erro) {
              console.log(erro);
              toast.error("Erro ao executar.");
            }
          },
        },
      ],
    });

  const verHtml = async (execucao) => {
    try {
      const r = await request(`/automacao/execucao/${execucao.exe_codigo}/html`, { method: "GET" });
      setHtmlAberto({ ...execucao, ...r });
    } catch (erro) {
      console.log(erro);
      toast.error("Não foi possível abrir o conteúdo.");
    }
  };

  const colunasAutomacao = useMemo(
    () => [
      { accessorKey: "aut_nome", header: "Automação", size: 200 },
      { accessorKey: "mod_nome", header: "Modelo", size: 160 },
      {
        accessorKey: "aut_hora",
        header: "Quando",
        size: 150,
        Cell: ({ row }) => (
          <Space direction="vertical" size={0}>
            <Text strong>{row.original.aut_hora}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {String(row.original.aut_dias || "")
                .split(",")
                .map((d) => NOME_DIAS[d])
                .filter(Boolean)
                .join(", ")}
            </Text>
          </Space>
        ),
      },
      {
        accessorKey: "aut_secao",
        header: "Editoria",
        size: 90,
        Cell: ({ cell }) => SECOES[cell.getValue()] || cell.getValue(),
      },
      {
        accessorKey: "aut_modo",
        header: "Modo",
        size: 90,
        Cell: ({ cell }) =>
          cell.getValue() === "publicar" ? (
            <Tag color="green">Publica direto</Tag>
          ) : (
            <Tag color="blue">Só rascunho</Tag>
          ),
      },
      {
        accessorKey: "aut_ativo",
        header: "Ativa",
        size: 70,
        Cell: ({ cell }) => (cell.getValue() ? <Tag color="green">Ativa</Tag> : <Tag>Parada</Tag>),
      },
      {
        accessorKey: "ultima_execucao",
        header: "Última execução",
        size: 170,
        Cell: ({ cell }) => (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {cell.getValue() || "nunca rodou"}
          </Text>
        ),
      },
    ],
    []
  );

  const colunasExecucao = useMemo(
    () => [
      { accessorKey: "aut_nome", header: "Automação", size: 180 },
      { accessorKey: "exe_inicio", header: "Quando", size: 150 },
      {
        accessorKey: "exe_status",
        header: "Resultado",
        size: 110,
        Cell: ({ cell }) => <Tag color={CORES_STATUS[cell.getValue()]}>{cell.getValue()}</Tag>,
      },
      {
        accessorKey: "exe_noticia",
        header: "Notícia",
        size: 90,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        accessorKey: "exe_motivo",
        header: "Observação",
        size: 400,
        Cell: ({ cell }) =>
          cell.getValue() ? (
            <Tooltip title={cell.getValue()}>
              <Text style={{ fontSize: 12 }}>{String(cell.getValue()).slice(0, 90)}</Text>
            </Tooltip>
          ) : (
            "—"
          ),
      },
    ],
    []
  );

  if (!carregou) return <Aguarde />;

  const abas = [
    {
      key: "automacoes",
      label: `Automações (${automacoes.length})`,
      children:
        automacoes.length > 0 ? (
          <MaterialReactTable
            columns={colunasAutomacao}
            data={automacoes}
            localization={MRT_Localization_PT_BR}
            enableRowActions
            positionActionsColumn="last"
            enableColumnFilterModes
            columnFilterModeOptions={null}
            initialState={{ pagination: { pageIndex: 0, pageSize: 20 } }}
            displayColumnDefOptions={{ "mrt-row-actions": { header: "", size: 190 } }}
            renderRowActions={({ row }) => (
              <div style={{ display: "flex", gap: 5, justifyContent: "end" }}>
                <Tooltip title="Rodar agora, sem esperar o horário">
                  <Button icon={<FaPlay />} onClick={() => rodarAgora(row.original)} />
                </Tooltip>
                <Button
                  icon={<AiFillEdit />}
                  onClick={() => {
                    setEmEdicao(row.original);
                    setFormAberto(true);
                  }}
                >
                  Editar
                </Button>
                <Button danger icon={<AiFillDelete />} onClick={() => excluir(row.original)} />
              </div>
            )}
            renderTopToolbarCustomActions={() => (
              <Content style={{ display: "flex", alignItems: "center", gap: 20, padding: 10 }}>
                <Typography.Title level={4} style={{ margin: 0 }}>
                  Notícias automáticas
                </Typography.Title>
                <Divider type="vertical" />
                <Button
                  type="primary"
                  icon={<FaPlus />}
                  size="large"
                  onClick={() => {
                    setEmEdicao({});
                    setFormAberto(true);
                  }}
                >
                  Nova automação
                </Button>
              </Content>
            )}
          />
        ) : (
          <Content
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 12,
              padding: 40,
            }}
          >
            <h2>Nenhuma automação cadastrada ainda.</h2>
            <Text type="secondary">
              Uma automação pega um modelo de matéria, preenche os valores com as cotações do banco
              e publica no horário marcado.
            </Text>
            <AiOutlineSearch size={48} color="#888" />
            <Button
              type="primary"
              icon={<FaPlus />}
              size="large"
              onClick={() => {
                setEmEdicao({});
                setFormAberto(true);
              }}
            >
              Nova automação
            </Button>
          </Content>
        ),
    },
    {
      key: "execucoes",
      label: `Histórico (${execucoes.length})`,
      children: (
        <MaterialReactTable
          columns={colunasExecucao}
          data={execucoes}
          localization={MRT_Localization_PT_BR}
          enableRowActions
          positionActionsColumn="last"
          initialState={{ pagination: { pageIndex: 0, pageSize: 25 } }}
          displayColumnDefOptions={{ "mrt-row-actions": { header: "", size: 110 } }}
          renderRowActions={({ row }) => (
            <Button size="small" onClick={() => verHtml(row.original)}>
              Ver conteúdo
            </Button>
          )}
          renderTopToolbarCustomActions={() => (
            <Content style={{ padding: 10 }}>
              <Text type="secondary">
                O que cada execução gerou — inclusive o motivo de uma matéria não ter sido publicada.
              </Text>
            </Content>
          )}
        />
      ),
    },
  ];

  return (
    <>
      <ModalAddAutomacao
        isOpen={formAberto}
        editData={emEdicao}
        onClose={() => setFormAberto(false)}
        onSaved={carregar}
      />

      <Tabs items={abas} defaultActiveKey="automacoes" style={{ padding: "0 10px" }} />

      <Drawer
        title={`Execução ${htmlAberto?.exe_codigo} — ${htmlAberto?.exe_status || ""}`}
        open={Boolean(htmlAberto)}
        onClose={() => setHtmlAberto(null)}
        width={760}
      >
        {htmlAberto?.exe_motivo && (
          <Content style={{ marginBottom: 16 }}>
            <Text type="danger">{htmlAberto.exe_motivo}</Text>
          </Content>
        )}
        {/* HTML gerado pelo próprio resolvedor e guardado na execução. */}
        <div dangerouslySetInnerHTML={{ __html: htmlAberto?.exe_html || "<i>sem conteúdo</i>" }} />
      </Drawer>
    </>
  );
}
